/**
 * This is the advanced version of ./src/utils/requestHelper.js
 * It is more readable and uses fetch instead of axios and it is synchronous.
 */
import { getCookie, removeCookie } from "./cookieHelper";
import { _MESSAGES, _SWAL_ICONS } from "./constants";
import Swal from "sweetalert2";

class HttpClientConfig {
  constructor(baseUrl, headers = {}) {
    this.baseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    this.headers = headers;
  }
}

class ErrorHandler {
  static async handleHttpError(status, message) {
    const errorConfig = {
      401: {
        title: "Unauthorized",
        message: _MESSAGES.identifiants_invalides,
        callback: () => {
          removeCookie("token");
          window.location.href = "/";
        },
      },
      403: {
        title: "Forbidden",
        message: _MESSAGES.unAuthorized,
        callback: () => (window.location.href = "/"),
      },
      404: {
        title: "Not Found",
        message: _MESSAGES.notFound,
        callback: () => {},
      },
    };

    const error = errorConfig[status];
    if (error) {
      await Swal.fire({
        icon: _SWAL_ICONS.error,
        title: error.title,
        text: error.message,
      });
      error.callback();
    }

    throw new Error(message);
  }
}

// Main HTTP client class
export class HttpClient {
  constructor(token = null) {
    this.config = new HttpClientConfig(
      process.env.REACT_APP_API_URL,
      this.getDefaultHeaders(token || getCookie("token")),
    );
  }

  getDefaultHeaders(token) {
    return {
      Accept: "application/json, application/ld+json",
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  getContentType(method, data) {
    if (method === "PATCH") return "application/merge-patch+json";
    if (data?.file) return "multipart/form-data";
    return "application/json";
  }

  async request(method, url, data = null, options = {}) {
    try {
      const cleanUrl = url.replace("api/", "").replace(/^\//, "");

      const fullUrl = new URL(this.config.baseUrl + cleanUrl);

      if (method === "GET" && data) {
        Object.keys(data).forEach((key) =>
          fullUrl.searchParams.append(key, data[key]),
        );
      }

      const headers = {
        ...this.config.headers,
        "Content-Type": this.getContentType(method, data),
      };

      const config = {
        method,
        headers,
        ...options,
        ...(data && method !== "GET" && { body: JSON.stringify(data) }),
      };

      const response = await fetch(fullUrl, config);

      if (!response.ok) {
        await ErrorHandler.handleHttpError(
          response.status,
          response.statusText,
        );
      }

      if (method !== "GET") {
        return response;
      }

      return await response.json();
    } catch (error) {
      console.error(`Request failed: ${error.message}`);
      throw error;
    }
  }

  // HTTP method shortcuts
  async get(url, params = null, options = {}) {
    return this.request("GET", url, params, options);
  }

  async post(url, data = null, options = {}) {
    return this.request("POST", url, data, options);
  }

  async put(url, data = null, options = {}) {
    return this.request("PUT", url, data, options);
  }

  async patch(url, data = null, options = {}) {
    return this.request("PATCH", url, data, options);
  }

  async delete(url, params = null, options = {}) {
    return this.request("DELETE", url, params, options);
  }

  async uploadFile(url, formData, options = {}) {
    return this.request("POST", url, formData, {
      ...options,
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
}
